import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';

type LoadState = 'loading' | 'ready' | 'saving' | 'error';

const MarketingPreferencesSection: React.FC = () => {
  const [optIn, setOptIn] = useState(false);
  const [optInAt, setOptInAt] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    apiClient.get('/Account/marketing-preferences')
      .then(r => {
        setOptIn(r.data.marketingOptIn ?? false);
        setOptInAt(r.data.marketingOptInAt ?? null);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  const handleToggle = async () => {
    const newValue = !optIn;
    setState('saving');
    setErrorMsg('');
    try {
      const response = await apiClient.put('/Account/marketing-preferences', { marketingOptIn: newValue });
      setOptIn(response.data.marketingOptIn);
      setOptInAt(response.data.marketingOptInAt ?? null);
      setState('ready');
    } catch {
      setErrorMsg('Failed to save preference. Please try again.');
      setState('error');
    }
  };

  if (state === 'loading') {
    return (
      <section className="mt-10 pt-8 border-t border-outline-variant">
        <div className="h-4 w-48 bg-surface-container rounded animate-pulse" />
      </section>
    );
  }

  return (
    <section className="mt-10 pt-8 border-t border-outline-variant">
      <h2 className="text-lg font-semibold text-on-surface mb-1">Communication preferences</h2>
      <p className="text-sm text-on-surface-variant mb-5">
        Control whether Buildon can send you marketing communications. This is separate from
        service notifications (job updates, messages) which are necessary to provide the service.
      </p>

      <div className="flex items-start gap-4 p-4 rounded-xl border border-outline-variant bg-surface-container-low">
        <button
          role="switch"
          aria-checked={optIn}
          onClick={handleToggle}
          disabled={state === 'saving'}
          className={`relative mt-0.5 w-12 h-6 rounded-full flex-shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 ${
            optIn ? 'bg-primary' : 'bg-outline-variant'
          }`}
        >
          <span
            className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
              optIn ? 'translate-x-6' : ''
            }`}
          />
        </button>
        <div>
          <p className="text-sm font-semibold text-on-surface">
            {optIn ? 'Opted in' : 'Opted out'}
          </p>
          <p className="text-sm text-on-surface-variant mt-0.5">
            {optIn
              ? 'You will receive occasional tips, platform news, and updates from Buildon.'
              : 'You will not receive marketing emails from Buildon.'}
          </p>
          {optIn && optInAt && (
            <p className="text-xs text-outline mt-1">
              Opted in on {new Date(optInAt).toLocaleDateString('en-ZA', { dateStyle: 'long' })}
            </p>
          )}
        </div>
      </div>

      {errorMsg && <p className="text-sm text-error mt-3">{errorMsg}</p>}

      <p className="text-xs text-outline mt-4">
        You can change this preference at any time. Withdrawing consent will take effect within 5 business days.
        Transactional emails (job status, messages, security alerts) are unaffected by this setting.
      </p>
    </section>
  );
};

export default MarketingPreferencesSection;
